"use client";

import { c as _c } from "react/compiler-runtime";
import { Delta } from "../delta-display";
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { type AggregatedMeasurementValue, type MeasurementBucketGranularity, formatBucketLabel } from "@/lib/measurement-aggregation";
import { BODY_MEASURES, BODY_MEASURE_LABELS, BODY_SEGMENTS, BODY_SEGMENT_LABELS, type BodyLayout, resolveBodyLayout } from "@/lib/measurement-body";
import { formatMeasurementValue } from "@/lib/measurement-calculations";
import { sortedDates } from "@/lib/measurement-utils";
import type { BodySegment, MeasurementGroupWithFields } from "@/types/measurement";
import BodyBars, { type SegmentCells } from "./body-bars";
import BodyFigure, { type BodySegmentVisual } from "./body-figure";

/**
 * Walk back to the first older bucket that has a value for *this* field. A
 * per-bucket previous would compare against a scan that never measured the
 * field, so each field gets its own baseline.
 */
function findOlderValue(dates: string[], valuesByDate: Record<string, AggregatedMeasurementValue[]>, fromIndex: number, fieldId: string): {
  value: AggregatedMeasurementValue;
  dateKey: string;
} | undefined {
  for (let i = fromIndex + 1; i < dates.length; i++) {
    const match = valuesByDate[dates[i]]?.find(v => v.fieldId === fieldId);
    if (match?.numericValue != null) return {
      value: match,
      dateKey: dates[i]
    };
  }
  return undefined;
}
function buildCellsBySegment(bodyLayout: BodyLayout, scanValueByFieldId: Map<string, AggregatedMeasurementValue>, dates: string[], valuesByDate: Record<string, AggregatedMeasurementValue[]>, scanIndex: number): Record<BodySegment, SegmentCells> {
  const result = {} as Record<BodySegment, SegmentCells>;
  for (const segment of BODY_SEGMENTS) {
    const bySegment: SegmentCells = {};
    const slotsForSegment = bodyLayout.slots[segment];
    for (const measure of BODY_MEASURES) {
      const entry = slotsForSegment[measure];
      if (!entry) continue;
      const value = scanValueByFieldId.get(entry.field.id);
      if (!value) continue;
      const older = scanIndex >= 0 ? findOlderValue(dates, valuesByDate, scanIndex, entry.field.id) : undefined;
      const delta = value.numericValue != null && older?.value.numericValue != null ? value.numericValue - older.value.numericValue : null;
      bySegment[measure] = {
        value,
        field: entry.field,
        delta,
        comparedTo: delta != null ? older?.dateKey ?? null : null
      };
    }
    result[segment] = bySegment;
  }
  return result;
}
function buildSegmentTooltip(segment: BodySegment, cells: SegmentCells, previousScanKey: string | null): ReactNode {
  return <div className="space-y-1">
      <p className="text-caption-strong text-foreground">
        {BODY_SEGMENT_LABELS[segment]}
      </p>
      {BODY_MEASURES.map(measure => {
      const cell = cells[measure];
      return <p key={measure} className="text-caption text-foreground-secondary">
            {BODY_MEASURE_LABELS[measure]}:{" "}
            <span className="text-foreground">
              {cell ? formatMeasurementValue(cell.value, cell.field) : "—"}
            </span>
            {cell && cell.delta != null && <Delta change={cell.delta} field={cell.field} size="xs" className="ml-1 inline-flex items-center gap-0.5 align-middle">
                <Delta.Icon />
                <Delta.Value />
                {cell.comparedTo && cell.comparedTo !== previousScanKey && <Delta.Since date={cell.comparedTo} className="ml-0.5" />}
              </Delta>}
          </p>;
    })}
    </div>;
}
interface BodyDisplayProps {
  group: MeasurementGroupWithFields;
  valuesByDate: Record<string, AggregatedMeasurementValue[]>;
  granularity: MeasurementBucketGranularity;
  timeZone: string;
}
export default function BodyDisplay(t0) {
  const $ = _c(44);
  const {
    group,
    valuesByDate,
    granularity,
    timeZone
  } = t0;
  let t1;
  if ($[0] !== group) {
    t1 = resolveBodyLayout(group);
    $[0] = group;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const bodyLayout = t1;
  const [openSegment, setOpenSegment] = useState(null);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [selectedScanDate, setSelectedScanDate] = useState(null);
  let t2;
  if ($[2] !== valuesByDate) {
    t2 = sortedDates(valuesByDate);
    $[2] = valuesByDate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const dates = t2;
  let t3;
  if ($[4] !== dates || $[5] !== selectedScanDate) {
    t3 = selectedScanDate && dates.includes(selectedScanDate) ? selectedScanDate : dates[0] ?? null;
    $[4] = dates;
    $[5] = selectedScanDate;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const effectiveScanDate = t3;
  let t4;
  if ($[7] !== dates || $[8] !== effectiveScanDate) {
    t4 = effectiveScanDate ? dates.indexOf(effectiveScanDate) : -1;
    $[7] = dates;
    $[8] = effectiveScanDate;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const scanIndex = t4;
  const previousScanKey = scanIndex >= 0 ? dates[scanIndex + 1] ?? null : null;
  let t5;
  if ($[10] !== effectiveScanDate || $[11] !== valuesByDate) {
    const values = effectiveScanDate ? valuesByDate[effectiveScanDate] ?? [] : [];
    t5 = new Map(values.map(_temp));
    $[10] = effectiveScanDate;
    $[11] = valuesByDate;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const scanValueByFieldId = t5;
  let t6;
  if ($[13] !== bodyLayout || $[14] !== dates || $[15] !== scanIndex || $[16] !== scanValueByFieldId || $[17] !== valuesByDate) {
    t6 = buildCellsBySegment(bodyLayout, scanValueByFieldId, dates, valuesByDate, scanIndex);
    $[13] = bodyLayout;
    $[14] = dates;
    $[15] = scanIndex;
    $[16] = scanValueByFieldId;
    $[17] = valuesByDate;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const cellsBySegment = t6;
  let result;
  if ($[19] !== cellsBySegment || $[20] !== previousScanKey) {
    result = {} as Record<BodySegment, BodySegmentVisual>;
    for (const segment of BODY_SEGMENTS) {
      const cells = cellsBySegment[segment];
      const shadeCell = cells.FAT_PCT_OF_NORMAL ?? cells.LEAN_PCT_OF_NORMAL ?? undefined;
      result[segment] = {
        severity: shadeCell?.value.outcome?.severity ?? null,
        tooltip: buildSegmentTooltip(segment, cells, previousScanKey)
      };
    }
    $[19] = cellsBySegment;
    $[20] = previousScanKey;
    $[21] = result;
  } else {
    result = $[21];
  }
  const segmentVisuals = result;
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (segment_0, open) => {
      setOpenSegment(prev => open ? segment_0 : prev === segment_0 ? null : prev);
    };
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handleSegmentOpenChange = t7;
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (segment_1, hovered) => {
      setHoveredSegment(prev_0 => hovered ? segment_1 : prev_0 === segment_1 ? null : prev_0);
    };
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const handleSegmentHover = t8;
  if (dates.length === 0) {
    let t9;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <p className="py-4 text-center text-body text-muted-foreground">No measurements recorded yet.</p>;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    return t9;
  }
  let t9;
  if ($[25] !== dates || $[26] !== effectiveScanDate || $[27] !== granularity || $[28] !== timeZone) {
    t9 = dates.length > 1 && effectiveScanDate && <div className="flex flex-wrap items-center justify-end gap-2"><Select value={effectiveScanDate} onValueChange={setSelectedScanDate}><SelectTrigger data-testid="body-scan-select" className="h-8 w-auto text-caption"><SelectValue /></SelectTrigger><SelectContent>{dates.map(date => <SelectItem key={date} value={date}>{formatBucketLabel(date, granularity, timeZone, "long")}</SelectItem>)}</SelectContent></Select></div>;
    $[25] = dates;
    $[26] = effectiveScanDate;
    $[27] = granularity;
    $[28] = timeZone;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== hoveredSegment || $[31] !== openSegment || $[32] !== segmentVisuals) {
    t10 = <div className="flex items-start justify-center"><BodyFigure segments={segmentVisuals} openSegment={openSegment} hoveredSegment={hoveredSegment} onSegmentOpenChange={handleSegmentOpenChange} onSegmentHover={handleSegmentHover} /></div>;
    $[30] = hoveredSegment;
    $[31] = openSegment;
    $[32] = segmentVisuals;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== cellsBySegment || $[35] !== hoveredSegment || $[36] !== previousScanKey) {
    t11 = <BodyBars cellsBySegment={cellsBySegment} hoveredSegment={hoveredSegment} onSegmentHover={handleSegmentHover} previousScanKey={previousScanKey} />;
    $[34] = cellsBySegment;
    $[35] = hoveredSegment;
    $[36] = previousScanKey;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== t10 || $[39] !== t11) {
    t12 = <div className="grid gap-6 @[44rem]:grid-cols-[150px_minmax(0,1fr)]">{t10}{t11}</div>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t12 || $[42] !== t9) {
    t13 = <div className="space-y-4 @container">{t9}{t12}</div>;
    $[41] = t12;
    $[42] = t9;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  return t13;
}
function _temp(v) {
  return [v.fieldId, v];
}